import { cn } from '../../lib/utils'

export interface SkeletonProps {
  className?: string
  variant?: 'text' | 'circular' | 'rectangular'
  width?: string | number
  height?: string | number
  count?: number
}

export function Skeleton({
  className,
  variant = 'text',
  width,
  height,
  count,
}: SkeletonProps) {
  const baseStyles = 'animate-pulse bg-neutral-200 dark:bg-neutral-800 rounded'

  if (count && count > 1) {
    return (
      <div className={cn('space-y-2', className)}>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className={cn(baseStyles, getVariantStyles(variant))}
            style={{ width, height }}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn(baseStyles, getVariantStyles(variant), className)}
      style={{ width, height }}
    />
  )
}

function getVariantStyles(variant: SkeletonProps['variant']) {
  switch (variant) {
    case 'circular':
      return 'rounded-full'
    case 'rectangular':
      return 'rounded-lg'
    default:
      return 'rounded'
  }
}

export function SkeletonText({ lines = 3, className, ...props }: Omit<SkeletonProps, 'variant' | 'count'> & { lines?: number }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" width={i === lines - 1 ? '60%' : '100%'} height="1rem" {...props} />
      ))}
    </div>
  )
}

export function SkeletonCard({ className, ...props }: SkeletonProps) {
  return (
    <div className={cn('card p-5 space-y-4', className)}>
      <div className="flex items-center gap-4">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="40%" height="1.25rem" />
          <Skeleton variant="text" width="60%" height="1rem" />
        </div>
      </div>
      <Skeleton variant="rectangular" width="100%" height={120} />
      <div className="flex items-center justify-between">
        <Skeleton variant="text" width="30%" height="1rem" />
        <Skeleton variant="rectangular" width={80} height={36} />
      </div>
    </div>
  )
}

export function SkeletonTable({ rows = 5, columns = 4, className, ...props }: SkeletonProps & { rows?: number; columns?: number }) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="table" {...props}>
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="th">
                <Skeleton variant="text" width="80%" height="1rem" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i}>
              {Array.from({ length: columns }).map((_, j) => (
                <td key={j} className="td">
                  <Skeleton variant="text" width="100%" height="1rem" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}